import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import type {
  PermissionCatalogue,
  PermissionCatalogueResponse,
  RolePermissionHistoryPage,
  RolePermissionHistoryResponse,
} from "../../interfaces/Roles.interface";

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

export const roleAPI = createApi({
  reducerPath: "roleAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetRoles", "tagGetRole", "tagGetRolesLight"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL ROLES */
    getRoles: builder.query({
      query: (body: any) => ({
        url: `/roles/?&page=${body.page}&limit=${body.limit}&sort=created_at&sort_direction=desc${
          body.searchAndFilterString || ""
        }`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
    //* GET ALL ROLES LIGHT */
    getRolesLight: builder.query({
      query: (body: any) => ({
        url: `/roles/light/${body.orgId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetRolesLight"],
    }),
    //* *********************************************************** */
    //* GET ROLES BY ORGANIZATION */
    getRolesByOrganization: builder.query({
      query: (body: any) => ({
        url: `/roles/?filter_target=organization_id&filter=${body.orgId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
    //* GET ONE ROLE */
    getRole: builder.query({
      query: (body: any) => ({
        url: `/roles/${body.roleId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetRole"],
    }),
    //* *********************************************************** */
    //* CREATE ROLE */
    postRole: builder.mutation({
      query: (body: any) => ({
        url: "/roles/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateRole" },
      invalidatesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
    //* EDIT ROLE */
    putRole: builder.mutation({
      query: (body: any) => ({
        url: `/roles/${body.id}`,
        method: "PUT",
        // Spread first, id last — see putUser for the same belt-and-suspenders
        // reasoning. UpdateRole serializer needs a valid id at pack time.
        body: { ...body.body, id: body.id },
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "UpdateRole" },
      invalidatesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
    //* DELETE ROLE */
    deleteRole: builder.mutation({
      query: (body: any) => ({
        url: `/roles/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
    //* GET PERMISSION CATALOGUE */
    // Every permission a role can hold, defined in LiftCloud's code. Plain JSON
    // (the server has no FlatBuffer converter for it), which the base query's
    // JSON fallback parses. It only changes when LiftCloud is upgraded, so a
    // cached copy is kept for an hour rather than dropped on unmount.
    getPermissionCatalogue: builder.query<PermissionCatalogue, { token: string }>({
      query: (body) => ({
        url: "/permission-catalogue/",
        headers: { authorization: body.token },
      }),
      transformResponse: (response: PermissionCatalogueResponse) => response.data,
      keepUnusedDataFor: 3600,
    }),
    //* *********************************************************** */
    //* GET ROLE PERMISSION HISTORY */
    // Changes to one role's permission decisions, newest first (LIFT-1092).
    // Plain JSON, parsed by the base query's JSON fallback. Paged by cursor:
    // pass the previous page's nextCursor to read the next, older page.
    getRolePermissionHistory: builder.query<
      RolePermissionHistoryPage,
      { roleId: string; token: string; limit?: number; cursor?: string | null }
    >({
      query: (body) => {
        const params = new URLSearchParams();
        if (body.limit) params.set("limit", String(body.limit));
        if (body.cursor) params.set("cursor", body.cursor);
        const qs = params.toString();
        return {
          url: `/roles/${encodeURIComponent(body.roleId)}/permission-history${qs ? `?${qs}` : ""}`,
          headers: { authorization: body.token },
        };
      },
      transformResponse: (response: RolePermissionHistoryResponse) => ({
        entries: response.data ?? [],
        nextCursor: response.next_cursor ?? null,
      }),
    }),
    //* *********************************************************** */
  }),
});

export const {
  useLazyGetRolesQuery,
  useGetRolesLightQuery,
  useLazyGetRolesLightQuery,
  useLazyGetRoleQuery,
  useLazyGetRolesByOrganizationQuery,
  usePostRoleMutation,
  usePutRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionCatalogueQuery,
  useLazyGetRolePermissionHistoryQuery,
} = roleAPI;
